import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, ExternalLink, CheckCircle2 } from 'lucide-react';

interface SystemAudioSetupGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSkip?: () => void;
}

const SystemAudioSetupGuide: React.FC<SystemAudioSetupGuideProps> = ({
  open,
  onOpenChange,
  onSkip
}) => {
  const [os, setOs] = useState<'windows' | 'mac' | 'linux'>('windows');

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('mac')) setOs('mac');
    else if (userAgent.includes('linux')) setOs('linux');
    else setOs('windows');
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>System Audio Sharing Setup</DialogTitle>
          <DialogDescription>
            Choose how you want to share your system audio with participants
          </DialogDescription>
        </DialogHeader>

        <Tabs value={os} onValueChange={(v) => setOs(v as any)} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="windows">Windows</TabsTrigger>
            <TabsTrigger value="mac">Mac</TabsTrigger>
            <TabsTrigger value="linux">Linux</TabsTrigger>
          </TabsList>

          <TabsContent value="windows" className="space-y-4">
            <div className="space-y-3">
              <h3 className="font-semibold">Option 1: Quick Start (Browser Tab Audio)</h3>
              <div className="space-y-2 text-sm">
                <div className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                  <p>No software installation needed</p>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                  <p>Share audio from browser tabs (YouTube, Spotify Web, etc.)</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold">Option 2: Full System Audio (VB-Cable)</h3>
              <div className="space-y-2 text-sm">
                <p className="font-medium">Steps:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Download and install VB-CABLE Virtual Audio Device</li>
                  <li>Restart your computer after installation</li>
                  <li>Set "CABLE Input" as your default playback device</li>
                  <li>In this app, select "CABLE Output" as your audio input</li>
                </ol>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open('https://vb-audio.com/Cable/', '_blank')}
              >
                <Download className="h-4 w-4 mr-2" />
                Download VB-CABLE
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="mac" className="space-y-4">
            <div className="space-y-3">
              <h3 className="font-semibold">Option 1: Quick Start (Browser Tab Audio)</h3>
              <div className="space-y-2 text-sm">
                <div className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                  <p>No software installation needed</p>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                  <p>Share audio from browser tabs</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold">Option 2: Full System Audio (BlackHole)</h3>
              <div className="space-y-2 text-sm">
                <p className="font-medium">Steps:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Download and install BlackHole 2ch</li>
                  <li>Open Audio MIDI Setup (Applications → Utilities)</li>
                  <li>Create a Multi-Output Device with BlackHole + Built-in Output</li>
                  <li>Set this Multi-Output as your system output</li>
                  <li>In this app, select "BlackHole 2ch" as input</li>
                </ol>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open('https://existential.audio/blackhole/', '_blank')}
              >
                <Download className="h-4 w-4 mr-2" />
                Download BlackHole
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="linux" className="space-y-4">
            <div className="space-y-3">
              <h3 className="font-semibold">Option 1: Quick Start (Browser Tab Audio)</h3>
              <div className="space-y-2 text-sm">
                <div className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                  <p>No software installation needed</p>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                  <p>Share audio from browser tabs</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold">Option 2: Full System Audio (PulseAudio)</h3>
              <div className="space-y-2 text-sm">
                <p className="font-medium">Steps:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Load the loopback module in PulseAudio</li>
                  <li>Run: <code className="bg-muted px-1 rounded">pactl load-module module-loopback</code></li>
                  <li>Use pavucontrol to route audio through the loopback</li>
                  <li>In this app, select the loopback device as input</li>
                </ol>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 justify-end">
          {onSkip && (
            <Button variant="outline" onClick={onSkip}>
              Use Quick Start
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SystemAudioSetupGuide;
